import { api } from "@/lib/apiClient";
import type { CurrentUser } from "@/lib/auth";
import {
  userIsAbrechnung,
  userIsBuero,
  userIsManagement,
  userIsMonteure,
  userHasGroup,
} from "@/lib/auth";
import {
  fetchProjectCompletions,
  filterOpenProjectCompletions,
} from "@/lib/projectCompletions";
import { fetchSupportActionRequiredCount, fetchSupportEingangCount } from "@/lib/support";
import {
  fetchMaterialBadgeCounts,
  fetchMaterialDeliveredUnseenCount,
} from "@/lib/material";
import { fetchAngebotsanfragenOpenCount } from "@/lib/angebotsanfragen";
import {
  fetchFeinplanungRequestsOpenCount,
  fetchMonteurProposalsOpenCount,
} from "@/lib/montageplanung";
import { fetchAnfragenAnswerCount } from "@/lib/anfragen";
import { fetchMahnwesenFaelligCount } from "@/lib/mahnwesen";
import { countKontakteOhneNummer } from "@/lib/kontakte";
import { fetchVeranstaltungUngelesenCount } from "@/lib/veranstaltungen";
import { fetchInvoiceApprovalOpenCount } from "@/lib/invoiceApproval";

/** Badge-Schlüssel wie in `static/navigation.js` / `static/partials/header.js`. */
export type NavBadgeKey =
  | "billing-unread"
  | "billing-assigned"
  | "billing-answered-unread"
  | "billing-questions"
  | "dokument-billing-questions"
  | "project-completions-open"
  | "lieferschein-billing-unread"
  | "lieferschein-billing-questions"
  | "support-eingang"
  | "support-action-required"
  | "material-open"
  | "material-open-urgent-fix"
  | "material-in-bearbeitung"
  | "material-delivered-unseen"
  | "angebote-open"
  | "montageplanung-feinplanung-requests"
  | "montageplanung-monteur-proposals"
  | "anfragen-answers"
  | "mahnwesen-faellig"
  | "mentions-unread"
  | "kontakte-ohne-nummer"
  | "veranstaltungen-ungelesen"
  | "rechnungsfreigabe-offen";

export type NavBadgeCounts = Partial<Record<NavBadgeKey, number>>;

export const NAV_BADGE_TITLE: Partial<Record<NavBadgeKey, string>> = {
  "billing-unread": "Unterschriebene Nachweise",
  "billing-assigned": "Rechnungsentwürfe dieses Jahres",
  "billing-answered-unread": "Beantwortete Rückfragen",
  "billing-questions": "Offene Rückfragen",
  "dokument-billing-questions": "Offene Dokument-Rückfragen",
  "project-completions-open": "Offene Fertigstellungen",
  "lieferschein-billing-unread": "Ungelesene Lieferscheine",
  "lieferschein-billing-questions": "Offene Lieferschein-Rückfragen",
  "support-eingang": "Tickets im Eingang",
  "support-action-required": "Neue Support-Antwort oder Rückfrage",
  "material-open-urgent-fix": "Unbearbeitete Fixtermine innerhalb von 3 Tagen",
  "material-open": "Offene Materialanforderungen",
  "material-in-bearbeitung": "Eigene Materialanforderungen in Bearbeitung",
  "material-delivered-unseen": "Gelieferte Materialanforderungen",
  "angebote-open": "Offene Angebote",
  "montageplanung-feinplanung-requests": "Offene Feinplanungsanfragen",
  "montageplanung-monteur-proposals": "Offene Monteursvorschläge",
  "anfragen-answers": "Neue Antworten auf Anfragen",
  "mahnwesen-faellig": "Fällige Mahnungen",
  "mentions-unread": "Ungelesene Erwähnungen",
  "kontakte-ohne-nummer": "Kontakte ohne Nummer",
  "veranstaltungen-ungelesen": "Ungelesene Einsätze",
  "rechnungsfreigabe-offen": "Rechnungen zur Freigabe",
}

/** Ton des Badges: offen/dringend, in Arbeit, erledigt bzw. neu eingetroffen. */
export const NAV_BADGE_CLASS: Record<NavBadgeKey, string> = {
  "billing-unread": "eh-nav-badge--danger",
  "billing-assigned": "eh-nav-badge--warning",
  "billing-answered-unread": "eh-nav-badge--success",
  "billing-questions": "eh-nav-badge--danger",
  "dokument-billing-questions": "eh-nav-badge--danger",
  "project-completions-open": "eh-nav-badge--danger",
  "lieferschein-billing-unread": "eh-nav-badge--danger",
  "lieferschein-billing-questions": "eh-nav-badge--danger",
  "support-eingang": "eh-nav-badge--danger",
  "support-action-required": "eh-nav-badge--warning",
  "material-open": "eh-nav-badge--danger",
  "material-open-urgent-fix": "eh-nav-badge--danger eh-nav-badge--urgent",
  "material-in-bearbeitung": "eh-nav-badge--warning",
  "material-delivered-unseen": "eh-nav-badge--success",
  "angebote-open": "eh-nav-badge--warning",
  "montageplanung-feinplanung-requests": "eh-nav-badge--warning",
  "montageplanung-monteur-proposals": "eh-nav-badge--warning",
  "anfragen-answers": "eh-nav-badge--success",
  "mahnwesen-faellig": "eh-nav-badge--danger",
  "mentions-unread": "eh-nav-badge--danger",
  "kontakte-ohne-nummer": "eh-nav-badge--danger",
  "veranstaltungen-ungelesen": "eh-nav-badge--danger",
  "rechnungsfreigabe-offen": "eh-nav-badge--warning",
};

export const MAHNWESEN_BADGE_EVENTS = [
  "dokumente_changed",
  "dokument_status_changed",
  "timesheets_changed",
  "hero_changed",
  "mahnwesen_changed",
] as const;

export function notifyMahnwesenBadgeRefresh(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event("mahnwesen_changed"));
}

export const BILLING_BADGE_EVENTS = [
  "timesheet_billing_seen",
  "timesheet_billing_assigned",
  "timesheet_billing_unassigned",
  "timesheet_status_changed",
  "timesheet_customer_signed",
  "timesheet_deleted",
  "timesheet_billing_question_created",
  "timesheet_billing_question_answered",
  "timesheets_changed",
  "dokumente_changed",
] as const;

export const PROJECT_COMPLETION_BADGE_EVENTS = [
  "auftrag_neu",
  "auftraege_changed",
  "projects_changed",
  "timesheets_changed",
  "timesheet_status_changed",
] as const;

export const SUPPORT_BADGE_EVENTS = [
  "support_ticket_created",
  "support_ticket_status_changed",
  "support_ticket_reply_created",
  "support_changed",
] as const;

export const MATERIAL_BADGE_EVENTS = [
  "material_anforderungen_changed",
  "material-anforderungen_changed",
  "material_anforderung_changed",
  "orders_changed",
] as const;

export function notifyMaterialBadgeRefresh(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event("material_anforderung_changed"));
}

export const ANGEBOTE_BADGE_EVENTS = ["angebotsanfragen_changed"] as const;

export const ANFRAGEN_ANSWERS_BADGE_EVENTS = [
  "anfragen_changed",
  "anfragen_mail_candidates_changed",
] as const;

export const MONTAGEPLANUNG_BADGE_EVENTS = [
  "montageplanung_feinplanung_requests_changed",
  "montageplanung_monteur_proposals_changed",
] as const;

export function notifyMontageplanungBadgeRefresh(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event("montageplanung_feinplanung_requests_changed"));
  window.dispatchEvent(new Event("montageplanung_monteur_proposals_changed"));
}

export const MENTIONS_BADGE_EVENTS = [
  "note_mention_created",
  "note_mention_deleted",
  "mentions_changed",
] as const;

export function notifyMentionsBadgeRefresh(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event("mentions_changed"));
}

export const VERANSTALTUNG_BADGE_EVENTS = ["veranstaltungen_changed"] as const;

export function notifyVeranstaltungBadgeRefresh(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event("veranstaltungen_changed"));
}

export const KONTAKTE_NUMMERN_BADGE_EVENTS = ["kontakte_nummern_changed"] as const;

export function notifyKontakteNummernBadgeRefresh(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event("kontakte_nummern_changed"));
}

async function fetchCount(path: string): Promise<number> {
  const data = await api.get<{ count?: number }>(path);
  return Number(data?.count || 0);
}

async function fetchBillingUnreadCount(): Promise<number> {
  return fetchCount("/timesheets/billing-unread-count");
}

async function fetchBillingAssignedCount(): Promise<number> {
  return fetchCount("/timesheets/billing-assigned-count");
}

async function fetchBillingUnreadAnswersCount(): Promise<number> {
  return fetchCount("/timesheets/billing-unread-answers-count");
}

async function fetchBillingQuestionsOpenCount(): Promise<number> {
  // Schlanker Count-Endpoint statt kompletter /timesheets/summary-Liste.
  return fetchCount("/timesheets/billing-questions-open-count");
}

async function fetchLieferscheinBillingUnreadCount(): Promise<number> {
  const rows = await api.get<Array<{ is_new_for_billing?: boolean }>>("/lieferscheine/summary");
  if (!Array.isArray(rows)) return 0;
  return rows.filter((r) => r.is_new_for_billing).length;
}

async function fetchLieferscheinBillingQuestionsOpenCount(): Promise<number> {
  return fetchCount("/lieferscheine/billing-questions-open-count");
}

async function fetchDokumentBillingQuestionsOpenCount(): Promise<number> {
  return fetchCount("/dokumente/billing-questions/open-count");
}

async function fetchProjectCompletionsOpenCount(): Promise<number> {
  const rows = await fetchProjectCompletions();
  return filterOpenProjectCompletions(rows).length;
}

async function fetchMentionsUnreadCount(): Promise<number> {
  return fetchCount("/mentions/me/unread-count");
}

export type NavBadgeFetchResult = {
  counts: NavBadgeCounts;
  /** Anzahl der Zähler, die wegen eines Fehlers nicht geladen werden konnten. */
  failed: number;
};

/** Lädt alle Nav-Badge-Zähler passend zur Benutzerrolle (analog header.js). */
export async function fetchNavBadgeCounts(user: CurrentUser | null): Promise<NavBadgeFetchResult> {
  const counts: NavBadgeCounts = {};
  const failures = { n: 0 };
  if (!user) return { counts, failed: 0 };

  const isAbrechnung = userIsAbrechnung(user);
  const isManagement = userIsManagement(user);

  const tasks: Array<Promise<void>> = [];

  if (isAbrechnung) {
    tasks.push(
      countKontakteOhneNummer()
        .then((data) => {
          counts["kontakte-ohne-nummer"] = Number(data?.count || 0);
        })
        .catch(() => {
          failures.n += 1;
        }),
      fetchBillingUnreadCount()
        .then((n) => {
          counts["billing-unread"] = n;
        })
        .catch(() => {
          failures.n += 1;
        }),
      fetchBillingAssignedCount()
        .then((n) => {
          counts["billing-assigned"] = n;
        })
        .catch(() => {
          failures.n += 1;
        }),
      fetchBillingUnreadAnswersCount()
        .then((n) => {
          counts["billing-answered-unread"] = n;
        })
        .catch(() => {
          failures.n += 1;
        }),
      fetchLieferscheinBillingUnreadCount()
        .then((n) => {
          counts["lieferschein-billing-unread"] = n;
        })
        .catch(() => {
          failures.n += 1;
        }),
    );
  }

  if (isManagement && !(isAbrechnung && !isManagement)) {
    tasks.push(
      fetchBillingQuestionsOpenCount()
        .then((n) => {
          counts["billing-questions"] = n;
        })
        .catch(() => {
          failures.n += 1;
        }),
      fetchLieferscheinBillingQuestionsOpenCount()
        .then((n) => {
          counts["lieferschein-billing-questions"] = n;
        })
        .catch(() => {
          failures.n += 1;
        }),
      fetchDokumentBillingQuestionsOpenCount()
        .then((n) => {
          counts["dokument-billing-questions"] = n;
        })
        .catch(() => {
          failures.n += 1;
        }),
    );
  }

  if (isManagement) {
    tasks.push(
      fetchProjectCompletionsOpenCount()
        .then((n) => {
          counts["project-completions-open"] = n;
        })
        .catch(() => {
          failures.n += 1;
        }),
    );
  }

  if (userIsBuero(user) || userHasGroup(user, "App-Admins")) {
    tasks.push(
      fetchMaterialBadgeCounts()
        .then(({ open, open_urgent_fix, in_bearbeitung }) => {
          const urgent = Math.max(0, open_urgent_fix);
          counts["material-open-urgent-fix"] = urgent;
          // Offene ohne die bereits als dringend gezählten Fixtermine.
          counts["material-open"] = Math.max(0, open - urgent);
          counts["material-in-bearbeitung"] = in_bearbeitung;
        })
        .catch(() => {
          failures.n += 1;
        }),
      fetchFeinplanungRequestsOpenCount()
        .then((n) => {
          counts["montageplanung-feinplanung-requests"] = n;
        })
        .catch(() => {
          failures.n += 1;
        }),
      fetchMonteurProposalsOpenCount()
        .then((n) => {
          counts["montageplanung-monteur-proposals"] = n;
        })
        .catch(() => {
          failures.n += 1;
        }),
    );
  }

  if (userIsMonteure(user)) {
    tasks.push(
      fetchMaterialDeliveredUnseenCount()
        .then((n) => {
          counts["material-delivered-unseen"] = n;
        })
        .catch(() => {
          failures.n += 1;
        }),
    );
  }

  if (
    userIsBuero(user) ||
    userHasGroup(user, "App-Admins") ||
    userHasGroup(user, "App-Tester")
  ) {
    tasks.push(
      fetchAnfragenAnswerCount()
        .then((n) => {
          counts["anfragen-answers"] = n;
        })
        .catch(() => {
          failures.n += 1;
        }),
    );
  }

  if (userIsBuero(user) || isManagement || userHasGroup(user, "App-Admins")) {
    tasks.push(
      fetchAngebotsanfragenOpenCount()
        .then((n) => {
          counts["angebote-open"] = n;
        })
        .catch(() => {
          failures.n += 1;
        }),
    );
  }

  if (userIsBuero(user) || userHasGroup(user, "App-Admins")) {
    tasks.push(
      fetchMahnwesenFaelligCount()
        .then((n) => {
          counts["mahnwesen-faellig"] = n;
        })
        .catch(() => {
          failures.n += 1;
        }),
      fetchVeranstaltungUngelesenCount()
        .then((n) => {
          counts["veranstaltungen-ungelesen"] = n;
        })
        .catch(() => {
          failures.n += 1;
        }),
      fetchInvoiceApprovalOpenCount()
        .then((n) => {
          counts["rechnungsfreigabe-offen"] = n;
        })
        .catch(() => {
          failures.n += 1;
        }),
    );
  }

  const isAppAdmin = ((user.groups || []) as string[]).some((g) =>
    /app-admins/i.test(String(g)),
  );
  if (isAppAdmin) {
    tasks.push(
      fetchSupportEingangCount()
        .then((n) => {
          counts["support-eingang"] = n;
        })
        .catch(() => {
          failures.n += 1;
        }),
    );
  }

  tasks.push(
    fetchSupportActionRequiredCount()
      .then((n) => {
        counts["support-action-required"] = n;
      })
      .catch(() => {
        failures.n += 1;
      }),
    fetchMentionsUnreadCount()
      .then((n) => {
        counts["mentions-unread"] = n;
      })
      .catch(() => {
        failures.n += 1;
      }),
  );

  await Promise.all(tasks);
  return { counts, failed: failures.n };
}
